import { httpApi } from '@api/http.api.ts';
import type { USER_TYPES } from '@constants/userTypes';

export type UserTypes = (typeof USER_TYPES)[keyof typeof USER_TYPES];

export interface UserInfoConsent {
    id: number;
    title: string;
    description: string;
    approvedDateUtc: string | null;
    isApproved: boolean;
}

export interface UserInfoPreferences {
    landingPage: string;
    dateFormat: string;
    timeFormat: string;
    notificationsApp: boolean;
    notificationsEmail: boolean;
    lastVisitedPathwayId: number | null;
}

export interface UserPersonalDataCommon {
    employeeId: string;
    dateOfBirthUTC: string;
    firstName: string;
    lastName: string;
    nationality: string;
    gender: string;
    email: string;
    backupEmail: string;
    phone: string;
}

export interface ShoreUserPersonalData extends UserPersonalDataCommon {
    dateCreatedUTC: string;
    lastChangedUTC: string;
    lastLoginUTC: string;
    active: boolean;
    avatarURI: string;
}

export interface CrewUserPersonalData extends UserPersonalDataCommon {
    dateCreatedUTC: string;
    lastChangedUTC: string;
    lastActivityUTC: string;
    requirementsStartUTC: string;
    active: boolean;
    avatarURI: string;
}

export interface UserPersonalDataShoreCrewCombined extends ShoreUserPersonalData, CrewUserPersonalData {}

export interface UserPersonalData {
    dateOfBirthUTC: string;
    email: string;
    firstName: string;
    lastName: string;
    backupEmail: string;
    phone: string;
    country: string;
    gender: string;
    avatarURI: string;
    title: string;
    location: string;
}

export interface EmploymentDetails {
    externalId: string;
    vesselRankId: number;
    clientVesselId: number;
    assessorId: number;
    managerId: number;
    trainingManagerId: number;
    clientOrganisationId: number;
    vesselDepartmentId: number;
    rankSinceUTC: string;
    locationSinceUTC: string;
    rankName: string;
}

export interface ShoreUserEmploymentDetails {
    id: number;
    employeeId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    nationalityId: number;
    nationalityName: string;
    genderId: number;
    genderName: string;
    primaryEmail: string;
    secondaryEmail: string | null;
    phone: string | null;
    titleId: number;
    titleName: string;
    titleSince: string | null;
    shoreTeamId: number | null;
    shoreTeamName: string | null;
    reportsToId: number | null;
    reportsToName: string | null;
    isVesselSuperintendent: boolean;
    joinedSince: string | null;
    lastActivityUTC: string | null;
    isActive: boolean;
    isBuiltIn: boolean;
    createdAtUTC: string;
    lastChanged: string;
}

export interface CrewUserEmploymentDetails {
    employeeId: string;
    manningOffice: string;
    crewingTeam: string;
    crewingTeamName: string;
    joinedSince: string;
    rankName: string;
    nextPlannedRankName: string;
    rankSince: string;
    genderId: number;
    genderName: string;
    signOn: string;
    actualSignOff: string;
    nextPlannedVessel: string;
    nextPlannedRank: string;
    plannedSignOn: string;
    isOnboard: boolean;
    // system data personal details
    createdAtUTC: string;
    lastChanged: string;
    lastActivityUTC: string;
    requirementsStartDate: string; // Requirements start
    rankDepartmentId: number;
    rankDepartmentName: string;
    vesselId: number;
    vesselName: string;
    nextPlannedVesselName: string;
    rankId: number;
    promotionRankId: number | null;
    nextPlannedRankId: number | null;
}

interface UserState {
    assignedTasks: number;
    deletionRequestedAtUTC: string | null;
    deletionRequestId: string | null;
    status: number;
    totalNewNotifications: number;
    score: number;
}

export interface UserInfoResponse {
    consents: UserInfoConsent[];
    preferences: UserInfoPreferences;
    userPersonalData: UserPersonalDataShoreCrewCombined;
    employmentDetails: {
        crewEmploymentDetails: CrewUserEmploymentDetails;
        shoreEmploymentDetails: ShoreUserEmploymentDetails;
        numberOfManagedCrew: number;
        numberOfManagedVessels: number;
    };
    userState: UserState;
    lastLoginUTC: string | null;
    lastPasswordUpdateUTC: string | null;
    userType: UserTypes;
    lastActivityUTC: string | null;
    // TODO: remove optionality when endpoint extended
    vessels?: number;
    crewTotal?: number;
}

export const getUserInfo = (): Promise<UserInfoResponse> =>
    httpApi.get<UserInfoResponse>(`me`).then(({ data }) => data);
